import { useEffect, useMemo, useState } from "react";
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import {
  getPanelMaps,
  listPanels,
  newPanel,
  type Panel,
  type PanelMaps,
  putPanel,
} from "@electro-planner/domain";
import { INDEXEDDB_PREFIX } from "../../lib/documentsRegistry.ts";
import { applyPanelSpecImport, takePendingImport } from "../export/share.ts";
import {
  applyTemplate,
  findTemplate,
  takePendingTemplate,
} from "../templates/index.ts";

/**
 * En Y.Doc per lokalt dokument (panel). Cache överlever React-remounts så
 * IndexedDB-persistensen inte sätts upp/rivs ner på varje render.
 */
type Cached = {
  doc: Y.Doc;
  maps: PanelMaps;
  persistence: IndexeddbPersistence;
  synced: Promise<void>;
};

const CACHE = new Map<string, Cached>();

export type UseYjsDocResult = {
  doc: Y.Doc;
  maps: PanelMaps;
  synced: boolean;
};

function getOrCreate(documentId: string): Cached {
  const existing = CACHE.get(documentId);
  if (existing) return existing;

  const doc = new Y.Doc();
  const maps = getPanelMaps(doc);
  const persistence = new IndexeddbPersistence(
    `${INDEXEDDB_PREFIX}:${documentId}`,
    doc,
  );

  const synced = persistence.whenSynced.then(() => {
    // Pending import-fil går först — användaren har explicit valt denna.
    const pendingImport = takePendingImport(documentId);
    if (pendingImport) {
      applyPanelSpecImport(doc, maps, pendingImport);
      return;
    }
    // Om användaren har bett om en mall till detta dokument-id: applicera.
    // Annars: seed en tom panel om dokumentet är helt tomt (nyskapat).
    const pendingKey = takePendingTemplate(documentId);
    if (pendingKey) {
      const template = findTemplate(pendingKey);
      if (template) {
        applyTemplate(doc, maps, template);
        return;
      }
    }
    if (listPanels(maps).length === 0) {
      const panel: Panel = newPanel({});
      doc.transact(() => {
        putPanel(maps, panel);
      });
    }
  });

  const entry: Cached = { doc, maps, persistence, synced };
  CACHE.set(documentId, entry);
  return entry;
}

export function useYjsDoc(documentId: string): UseYjsDocResult {
  const cached = useMemo(() => getOrCreate(documentId), [documentId]);
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    let cancelled = false;
    cached.synced.then(() => {
      if (!cancelled) setSynced(true);
    });
    return () => {
      cancelled = true;
    };
  }, [cached]);

  return { doc: cached.doc, maps: cached.maps, synced };
}
